import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { ApiError, api } from '../api';
import { tr, useLang } from '../i18n';
import { adminFallback, messageFor, registroVals, ruleTicks, type AuthRoute } from './auth-logic';

// The prototype's own form styles ("Portal Zenit+" / "Acceso" in prototype/Liga Inversora Zenit.dc.html), as they are.
const S: Record<string, CSSProperties> = {
  // The prototype's outer wrapper gives every screen its font, line height and figures (QA 26 Oct 2026: these showed in Times).
  main: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#F6F5F3', color: '#03033B',
    fontFamily: "Nudica,'Instrument Sans',system-ui,sans-serif", fontSize: 16, lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' },
  form: { width: '100%', maxWidth: 420, background: '#FFFFFF', border: '1px solid #DCDAD5', borderRadius: 12, padding: 32, display: 'flex', flexDirection: 'column', gap: 16 },
  logo: { width: 160, height: 'auto', background: '#03033B', borderRadius: 8, padding: 10 },
  h1: { margin: 0, fontFamily: "'Bree Serif',Georgia,serif", fontWeight: 400, fontSize: 32 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 },
  input: { fontSize: 16, padding: 12, border: '1px solid #9C9BB0', borderRadius: 8, background: '#FFFFFF', color: '#03033B' },
  primary: { background: '#03033B', color: '#FFFFFF', border: 'none', borderRadius: 999, padding: 14, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  link: { background: 'none', border: 'none', color: '#03033B', textDecoration: 'underline', fontSize: 14, cursor: 'pointer', padding: 0 },
  err: { margin: 0, color: '#C7321A', fontSize: 14 },
  text: { margin: 0, fontSize: 15 },
  muted: { margin: 0, color: '#4A4A6A', fontSize: 14 },
  rules: { margin: '-6px 0 0', padding: 0, listStyle: 'none', fontSize: 13, color: '#4A4A6A', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' },
};
const disabledBtn: CSSProperties = { ...S.primary, background: '#DCDAD5', color: '#4A4A6A', cursor: 'not-allowed' };

export interface AuthNav { go(r: AuthRoute | null, path?: string): void; signedIn(role: 'participant' | 'admin'): void; signedOut(): void }

const codeOf = (e: unknown) => (e instanceof ApiError ? e.error : 'network');

function Card({ title, label, onSubmit, children }: { title: string; label: string; onSubmit?: () => void; children: ReactNode }) {
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit?.(); };
  return (
    <main data-screen-label={label} style={S.main}>
      <form onSubmit={submit} noValidate style={S.form}>
        <img src="/assets/logo-liga-zenit.png" alt="Liga Inversora Zenit" style={S.logo} />
        <h1 style={S.h1}>{tr(title)}</h1>
        {children}
      </form>
    </main>
  );
}
function Field({ label, ...p }: { label: string; type?: string; value: string; onChange(v: string): void; autoComplete?: string; invalid?: boolean }) {
  return (
    <label style={S.label}>{tr(label)}
      <input type={p.type ?? 'text'} value={p.value} onChange={e => p.onChange(e.target.value)} autoComplete={p.autoComplete} aria-invalid={p.invalid || undefined}
        style={{ ...S.input, ...(p.invalid ? { borderColor: '#C7321A' } : {}) }} />
    </label>
  );
}
const Err = ({ msg }: { msg: string }) => (msg ? <p role="alert" style={S.err}>{tr(msg)}</p> : null);
const Submit = ({ label, enabled, busy }: { label: string; enabled: boolean; busy: boolean }) =>
  <button type="submit" disabled={!enabled || busy} style={enabled && !busy ? S.primary : disabledBtn}>{tr(label)}</button>;
function Rules({ pw }: { pw: string }) {
  return <ul style={S.rules} aria-label={tr('Requisitos de la contraseña')}>{ruleTicks(pw).map(r => (
    <li key={r.label}><span aria-hidden style={{ color: r.ok ? '#007A6E' : '#9C9BB0', fontWeight: 600 }}>{r.ok ? '✓' : '○'} </span>{tr(r.label)}</li>))}</ul>;
}
/** Runs a request with a busy flag; an API error becomes the screen's message. */
function useSubmit() {
  const [busy, setBusy] = useState(false), [err, setErr] = useState('');
  const run = async (f: () => Promise<void>) => {
    if (busy) return; setBusy(true); setErr('');
    try { await f(); } catch (e) { setErr(messageFor(codeOf(e))); } finally { setBusy(false); }
  };
  return { busy, err, setErr, run };
}

export function Acceso({ nav }: { nav: AuthNav }) {
  const [email, setEmail] = useState(''), [password, setPassword] = useState(''), s = useSubmit();
  const submit = () => s.run(async () => {
    try { nav.signedIn((await api.login({ email, password })).role); }
    catch (e) { if (codeOf(e) === 'unverified') return nav.go({ screen: 'revisa', email }); throw e; }
  });
  return (
    <Card title="Acceso" label="Acceso" onSubmit={submit}>
      <Field label="Correo" type="email" value={email} onChange={setEmail} autoComplete="email" />
      <Field label="Contraseña" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="button" style={S.link} onClick={() => nav.go({ screen: 'recuperar' })}>{tr('¿Has olvidado tu contraseña?')}</button></div>
      <Err msg={s.err} />
      <Submit label="Entrar" enabled={!!email.trim() && !!password} busy={s.busy} />
      <p style={{ ...S.muted, textAlign: 'center' }}>{tr('¿Primera vez?')} <button type="button" style={S.link} onClick={() => nav.go({ screen: 'registro' })}>{tr('Crea tu cuenta')}</button></p>
      <button type="button" style={S.link} onClick={() => nav.go(null, '/')}>{tr('Volver')}</button>
    </Card>
  );
}

export function Registro({ nav }: { nav: AuthNav }) {
  const [f, setF] = useState({ nombre: '', apellido: '', email: '', password: '', password2: '' }), s = useSubmit(), [bad, setBad] = useState('');
  const v = registroVals(f), set = (k: keyof typeof f) => (x: string) => setF(o => ({ ...o, [k]: x }));
  const submit = () => s.run(async () => {
    setBad('');
    try { await api.signup({ nombre: f.nombre, apellido: f.apellido, email: f.email, password: f.password }); nav.go({ screen: 'revisa', email: f.email.trim() }); }
    catch (e) { if (codeOf(e) === 'not_member') setBad('email'); throw e; }
  });
  return (
    <Card title="Crear cuenta" label="Crear cuenta" onSubmit={() => v.canSubmit && submit()}>
      <Field label="Nombre" value={f.nombre} onChange={set('nombre')} autoComplete="given-name" />
      <Field label="Apellidos" value={f.apellido} onChange={set('apellido')} autoComplete="family-name" />
      <Field label="Correo de Zenit+" type="email" value={f.email} onChange={x => { setBad(''); set('email')(x); }} autoComplete="email" invalid={bad === 'email'} />
      <Field label="Contraseña" type="password" value={f.password} onChange={set('password')} autoComplete="new-password" />
      <Rules pw={f.password} />
      <Field label="Repite la contraseña" type="password" value={f.password2} onChange={set('password2')} autoComplete="new-password" />
      <Err msg={v.mismatch || s.err} />
      <Submit label="Crear cuenta" enabled={v.canSubmit} busy={s.busy} />
      <p style={{ ...S.muted, textAlign: 'center' }}>{tr('¿Ya tienes cuenta?')} <button type="button" style={S.link} onClick={() => nav.go({ screen: 'acceso' })}>{tr('Entra')}</button></p>
    </Card>
  );
}

export function RevisaCorreo({ nav, email }: { nav: AuthNav; email: string }) {
  const s = useSubmit(), [sent, setSent] = useState(false);
  return (
    <Card title="Revisa tu correo" label="Revisa tu correo" onSubmit={() => s.run(async () => { await api.resend({ email }); setSent(true); })}>
      <p style={S.text}>{tr('Te hemos enviado un enlace a')} <b>{email}</b>{tr('. Ábrelo para confirmar tu correo y entrar en la Liga.')}</p>
      <p style={S.muted}>{tr('El enlace caduca en 24 horas. Si no lo ves, mira en spam.')}</p>
      {sent && !s.err ? <p role="status" style={S.text}>{tr('Te hemos enviado el correo otra vez.')}</p> : null}
      <Err msg={s.err} />
      <Submit label="Reenviar el correo" enabled={!!email} busy={s.busy} />
      <button type="button" style={S.link} onClick={() => nav.go({ screen: 'acceso' })}>{tr('Volver a Acceso')}</button>
    </Card>
  );
}

export function Verificar({ nav, token }: { nav: AuthNav; token?: string }) {
  const [err, setErr] = useState(''), started = useRef(false);
  useEffect(() => {
    if (started.current) return; started.current = true; // a single use: the link works once
    if (!token) { setErr(messageFor('token')); return; }
    api.verify({ token }).then(r => nav.signedIn(r.role), e => setErr(messageFor(codeOf(e))));
  }, [token, nav]);
  return (
    <Card title="Confirmar correo" label="Confirmar correo">
      {err ? <><Err msg={err} /><p style={S.muted}>{tr('Si aún no has confirmado tu correo, entra y te enviaremos un enlace nuevo.')}</p>
        <button type="button" style={S.primary} onClick={() => nav.go({ screen: 'acceso' })}>{tr('Ir a Acceso')}</button></>
        : <p style={S.text}>{tr('Confirmando tu correo…')}</p>}
    </Card>
  );
}

export function Recuperar({ nav }: { nav: AuthNav }) {
  const [email, setEmail] = useState(''), [sent, setSent] = useState(false), s = useSubmit();
  return (
    <Card title="Recuperar contraseña" label="Recuperar contraseña" onSubmit={() => s.run(async () => { await api.forgot({ email }); setSent(true); })}>
      <p style={S.muted}>{tr('Escribe tu correo y te enviaremos un enlace para crear una contraseña nueva.')}</p>
      <Field label="Correo" type="email" value={email} onChange={x => { setSent(false); setEmail(x); }} autoComplete="email" />
      <Err msg={s.err} />
      <Submit label="Enviar enlace" enabled={!!email.trim()} busy={s.busy} />
      {sent && !s.err ? <p role="status" style={S.text}>{tr('Si hay una cuenta con ese correo, te hemos enviado un enlace. Caduca en 1 hora.')}</p> : null}
      <button type="button" style={S.link} onClick={() => nav.go({ screen: 'acceso' })}>{tr('Volver a Acceso')}</button>
    </Card>
  );
}

export function ContrasenaNueva({ nav, token }: { nav: AuthNav; token?: string }) {
  const [pw, setPw] = useState(''), [pw2, setPw2] = useState(''), s = useSubmit();
  const strong = ruleTicks(pw).every(r => r.ok), mismatch = pw2 && pw !== pw2 ? 'Las contraseñas no coinciden.' : '';
  const submit = () => s.run(async () => { if (!token) throw new ApiError(410, 'token'); nav.signedIn((await api.reset({ token, password: pw })).role); });
  return (
    <Card title="Contraseña nueva" label="Contraseña nueva" onSubmit={() => strong && !mismatch && pw2 && submit()}>
      <Field label="Contraseña nueva" type="password" value={pw} onChange={setPw} autoComplete="new-password" />
      <Rules pw={pw} />
      <Field label="Repite la contraseña" type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
      <Err msg={mismatch || s.err} />
      <Submit label="Guardar y entrar" enabled={strong && !!pw2 && !mismatch} busy={s.busy} />
      {s.err === messageFor('token') ? <button type="button" style={S.link} onClick={() => nav.go({ screen: 'recuperar' })}>{tr('Pedir un enlace nuevo')}</button> : null}
    </Card>
  );
}

export function Admin({ nav }: { nav: AuthNav }) {
  const [me, setMe] = useState<{ nombre: string; apellido: string; email: string } | null>(null);
  useEffect(() => { api.adminSession().then(setMe, e => (adminFallback(e) === 'participant' ? nav.signedIn('participant') : nav.go({ screen: 'acceso' }))); }, [nav]);
  return (
    <Card title="Administración" label="Administración">
      {me ? <><p style={S.text}>{me.nombre} {me.apellido} · {me.email}</p>
        <p style={S.muted}>{tr('El módulo de administración se definirá más adelante.')}</p></> : <p style={S.muted}>{tr('Cargando…')}</p>}
      <button type="button" style={S.primary} onClick={() => { void api.logout().finally(() => nav.signedOut()); }}>{tr('Salir')}</button>
    </Card>
  );
}

export function AuthScreens({ route, nav }: { route: AuthRoute; nav: AuthNav }) {
  useLang(); // re-render on a language change
  switch (route.screen) {
    case 'acceso': return <Acceso nav={nav} />;
    case 'registro': return <Registro nav={nav} />;
    case 'revisa': return <RevisaCorreo nav={nav} email={route.email ?? ''} />;
    case 'verificar': return <Verificar nav={nav} token={route.token} />;
    case 'recuperar': return <Recuperar nav={nav} />;
    case 'contrasena': return <ContrasenaNueva nav={nav} token={route.token} />;
    case 'admin': return <Admin nav={nav} />;
  }
}
